import React from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces';
import useFetchDeviceCrmByEan from '../../hooks/useFetchDeviceCrmByEan';
import useAddContactToCustomer from '../../hooks/useAddContactToCustomer';
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";

interface ContactAddControlProps {
  show: boolean;
  handleClose: () => void;
  ean: string | undefined;
  customer: Customer | null;
  onSuccess: () => void;
}

const ContactAddModal: React.FC<ContactAddControlProps> = ({ show, ean, customer, handleClose, onSuccess }) => {
  const { t } = useTranslation();
  const { crmResponse, error: fetchError } = useFetchDeviceCrmByEan({ ean: ean });
  const { addContactToCustomer, error: updateError } = useAddContactToCustomer();

  const contactConfigSchema = yup.object().shape({
    firstName: yup.string().required(t("required")),
    lastName: yup.string().required(t("required")),
    adress: yup.string().required(t("required")),
    zipCode: yup.string().required(t("required")),
    city: yup.string().required(t("required")),
    country: yup.string().required(t("required")),
    eMail: yup.string().required(t("required")).matches(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, t("invalidEmail")),
    phone: yup.string().required(t("required")),
  });

  type MyFormData = yup.InferType<typeof contactConfigSchema>;

  const { register, handleSubmit: hookFormSubmit, formState: { errors } } = useForm<MyFormData, any, any>({
    mode: 'all',
    resolver: yupResolver(contactConfigSchema),
  });

  const handleSubmit = async (formData: MyFormData) => {
    const finalData = {
      ...formData,
      id: 0,
      customer: { id: customer ? customer.id : 0 }
    }
    console.log(finalData);
    const data = await addContactToCustomer(finalData);
    if (data) {
      handleClose();
      onSuccess();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size='lg'>
      <Modal.Header className='d-flex justify-content-center'>
        <Modal.Title className='text-secondary fs-2'>{customer?.siren} {t('devices.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='form-contact' onSubmit={hookFormSubmit(handleSubmit)}>
          {fetchError && <Alert variant="danger">{fetchError}</Alert>}
          {updateError && <Alert variant="danger">{updateError}</Alert>}

          <Row className="g-4">
            <Col md={6}>
              <Form.Group controlId="formFirstName">
                <Form.Label className="fs-5 fw-medium text-secondary">{t("userProfile.firstName")}</Form.Label>
                <Form.Control
                  type="text"
                  {...register("firstName")}
                  isInvalid={!!errors.firstName}
                  className='text-secondary bg-light fw-medium text-capitalize'
                  placeholder={t("enterFirstName")}
                />
                <Form.Control.Feedback type='invalid'>{errors.firstName?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formLastName">
                <Form.Label className="fs-5 fw-medium text-secondary">{t("userProfile.lastName")}</Form.Label>
                <Form.Control
                  type="text"
                  {...register("lastName")}
                  isInvalid={!!errors.lastName}
                  className='text-secondary bg-light fw-medium text-capitalize'
                  placeholder={t("enterLastName")}
                />
                <Form.Control.Feedback type='invalid'>{errors.lastName?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={9}>
              <Form.Group controlId="formAddress">
                <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.customer.label.address")}</Form.Label>
                <Form.Control
                  type="text"
                  {...register("adress")}
                  isInvalid={!!errors.adress}
                  className='text-secondary bg-light fw-medium text-capitalize'
                  placeholder={t("devices.crm.enterAddress")}
                />
                <Form.Control.Feedback type='invalid'>{errors.adress?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formZipCode">
                <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.crm.zipCode")}</Form.Label>
                <Form.Control
                  type="text"
                  {...register("zipCode")}
                  isInvalid={!!errors.zipCode}
                  className='text-secondary bg-light fw-medium text-capitalize'
                  placeholder={t("devices.crm.enterZipCode")}
                />
                <Form.Control.Feedback type='invalid'>{errors.zipCode?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formCity">
                <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.crm.city")}</Form.Label>
                <Form.Control
                  type="text"
                  {...register("city")}
                  isInvalid={!!errors.city}
                  className='text-secondary bg-light fw-medium text-capitalize'
                  placeholder={t("devices.crm.enterCity")}
                />
                <Form.Control.Feedback type='invalid'>{errors.city?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formCountry">
                <Form.Label className="fs-5 fw-medium text-secondary">{t("devices.crm.country")}</Form.Label>
                <Form.Control
                  type="text"
                  {...register("country")}
                  isInvalid={!!errors.country}
                  className='text-secondary bg-light fw-medium text-capitalize'
                  placeholder={t("devices.crm.enterCountry")}
                />
                <Form.Control.Feedback type='invalid'>{errors.country?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formEmail">
                <Form.Label className="fs-5 fw-medium text-secondary">{t("email")}</Form.Label>
                <Form.Control
                  type="text"
                  {...register("eMail")}
                  isInvalid={!!errors.eMail}
                  className='text-secondary bg-light fw-medium text-lowercase'
                  placeholder={t("enterEmail")}
                />
                <Form.Control.Feedback type='invalid'>{errors.eMail?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formPhone">
                <Form.Label className="fs-5 fw-medium text-secondary">{t("phone")}</Form.Label>
                <Form.Control
                  type="number"
                  {...register("phone")}
                  isInvalid={!!errors.phone}
                  className='text-secondary bg-light fw-medium'
                  placeholder={t("enterPhone")}
                />
                <Form.Control.Feedback type='invalid'>{errors.phone?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type='submit' className='px-2 fw-medium' form='form-contact' variant="secondary">
          {t("submit")}
        </Button>
        <Button className='px-2 fw-medium' variant="primary" onClick={handleClose}>
          {t("close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ContactAddModal;
